import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Dialog,
  IconButton,
  useMediaQuery,
  useTheme,
  Box,
  DialogContent,
  AppBar,
  Typography,
  Toolbar,
} from "@mui/material";
import { Close, Build } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useGridApiRef } from "@mui/x-data-grid-pro"
import { authHeader, getLocalStorageItem, setLocalStorageItem } from "../../_helpers";
import { apps, LOCAL_STORAGE_GRID_KEYS } from "../../_constants";
import DataGridV2 from "../../components/DataGridV2";
import { LabelColumnV2, LinkColumnV2 } from "../../components/DataGridV2/DataColumnsV2";
import { getGridContextMenuList } from "../../_helpers/getGridContextMenuList";
import i18n from "../../i18n";
import useGridDataModalHandler from "../../_hooks/useGridDataModalHandler";
import { INITIAL_ROW_SELECTION_MODEL } from "../../components/DataGridV2/constants";
const { webApiUrl } = window.appConfig;

const defaultOplosserFilter = {
  items: [
    {
      field: "isOplosser",
      operator: "is",
      value: "true",
    }
  ],
  logicOperator: "and",
  quickFilterValues: [],
  quickFilterLogicOperator: "and",
};

const SelectRelationModal = ({
  handleCloseRelationModal,
  organisationId,
  projectId,
  onSelect
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const apiRef = useGridApiRef();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [relationsResult, setRelationsResult] = useState([]);
  const [rowSelectionModel, setRowSelectionModel] = useState(INITIAL_ROW_SELECTION_MODEL);
  const [isLoading, setIsLoading] = useState(false)
  const app = useSelector((state) => state.app);
  const keyPrefix = app === apps.aftercare ? "AFTERCARE" : app === apps.constructionQuality ? "CONSTRUCTION_QUALITY" : "GENERAL";
  const [paginationModel, setPaginationModel] = useState(() => getLocalStorageItem(
    LOCAL_STORAGE_GRID_KEYS.SELECT_RELATION_MODAL_PAGINATION[keyPrefix], undefined
  ))
  const {
    filterModel,
    handleFilter,
  } = useGridDataModalHandler({
    filterModal: getLocalStorageItem(
      LOCAL_STORAGE_GRID_KEYS.SELECT_RELATION_MODAL_COLUMN_FILTER[keyPrefix], undefined
    ) ?? defaultOplosserFilter,
  });

  const GetRelationsList = () => {
    setIsLoading(true)
    const url =
      webApiUrl +
      "api/Organisation/GetRelationsForOrganisationWithDetails/" +
      encodeURI(organisationId) + "/" + encodeURI(projectId);
    const requestOptions = {
      method: "GET",
      headers: authHeader(),
    };

    fetch(url, requestOptions)
      .then((Response) => Response.json())
      .then((findResponse) => {
        setRelationsResult(findResponse.map((item) => ({
          ...item,
          id: item.id,
          oplosserSearchField: item.isOplosser ? t("general.yes") : "",
        })));
        setIsLoading(false)
      })
      .catch(() => {
        setIsLoading(false)
      });
  };

  useEffect(() => {
    if (organisationId && projectId) {
      GetRelationsList();
    }
  }, []);

  const getOptions = getGridContextMenuList({
    filterKey: LOCAL_STORAGE_GRID_KEYS.SELECT_RELATION_MODAL_COLUMN_FILTER[keyPrefix],
    i18n,
    t,
    apiRef,
    mode: "client"
  })

  const columns = useMemo(() => {
    return [
      {
        field: "functionName",
        headerName: t("general.function"),
        flex: 1,
        sortable: false,
        download: true,
        renderCell: (params) => {
          const rowData = params.row;
          return (
            <LabelColumnV2
              tooltipLabel={rowData.functionName}
              label={rowData.functionName}
              request={rowData}
              cellSubMenuOptions={{ options: getOptions({ field: "functionName", isQuickFilterDisplay: Boolean(rowData.functionName), sortable: false }), extraData: { fieldName: "functionName" } }}
            />
          );
        },
      },
      {
        field: "name",
        headerName: t("general.name"),
        flex: 1,
        sortable: false,
        download: true,
        renderCell: (params) => {
          const rowData = params.row;
          return (
            <LinkColumnV2
              tooltipLabel={rowData.name}
              label={rowData.name}
              onClick={() => {
                onSelect(rowData)
                handleCloseRelationModal()
              }}
              to="#"
              request={rowData}
              cellSubMenuOptions={{ options: getOptions({ field: "name", isQuickFilterDisplay: Boolean(rowData.name), sortable: false }), extraData: { fieldName: "name" } }}
            />
          );
        },
      },
      {
        field: "isOplosser",
        headerName: t("general.resolvers"),
        flex: 0,
        width: 0,
        sortable: false,
        type: "boolean",
        filterable: true,
        getApplyQuickFilterFn: (searchVal) => {
          return (value, row) => {
            return row?.oplosserSearchField?.toLowerCase().includes(searchVal.toLowerCase());
          };
        },
      },
    ];
  }, [t, getOptions]);

  const onFilterModelChange = useCallback((newFilterMModel) => {
    handleFilter(newFilterMModel, (newFilterMModel) => {
      setLocalStorageItem(
        LOCAL_STORAGE_GRID_KEYS.SELECT_RELATION_MODAL_COLUMN_FILTER[keyPrefix],
        newFilterMModel
      );
    })
  }, [handleFilter, keyPrefix])

  const handleClickOplossers = () => {
    let cloneFilterModal = filterModel;
    if (cloneFilterModal && cloneFilterModal.items?.length) {
      const filterIndex = cloneFilterModal?.items?.findIndex(
        (item) => item.field === "isOplosser"
      );
      if (filterIndex !== -1) {
        if (cloneFilterModal.items[filterIndex].operator === "is" && cloneFilterModal.items[filterIndex].value === "true") {
          const newFilterItems = cloneFilterModal?.items?.filter(
            (item) => item.field !== "isOplosser"
          );
          cloneFilterModal = {
            ...cloneFilterModal,
            items: newFilterItems,
          };
        } else {
          cloneFilterModal.items[filterIndex].operator = "is"
          cloneFilterModal.items[filterIndex].value = "true"
        }
      } else {
        cloneFilterModal.items.push({
          field: "isOplosser",
          operator: "is",
          value: "true",
        });
      }
    } else {
      cloneFilterModal = { ...defaultOplosserFilter };
    }

    handleFilter(cloneFilterModal, (newFilter) => {
      setLocalStorageItem(
        LOCAL_STORAGE_GRID_KEYS.SELECT_RELATION_MODAL_COLUMN_FILTER[keyPrefix],
        newFilter
      );
    });
  };

  const quickFilterItems = [
    {
      title: t("general.resolvers"),
      onClick: handleClickOplossers,
      isSelected: filterModel?.items?.find(
        (item) => item.field === "isOplosser"
      )?.value === "true",
      icon: (props) => <Build {...props} />
    },
  ]

  const getToolbarProps = useMemo(() => {
    const toolbarProps = {
      apiRef: apiRef,
      columns: columns,
      quickFilterItems: quickFilterItems,
      selectedRows: rowSelectionModel
    };
    return toolbarProps;
  }, [apiRef, quickFilterItems, rowSelectionModel, t]);


  const onPaginationModelChange = useCallback((newPaginationModel) => {
    setPaginationModel(newPaginationModel)
    setLocalStorageItem(
      LOCAL_STORAGE_GRID_KEYS.SELECT_RELATION_MODAL_PAGINATION[keyPrefix],
      newPaginationModel
    );
  }, [keyPrefix])

  const defaultColumnVisibility = useMemo(() => ({
    isOplosser: false,
  }), []);

  return (
    <Dialog
      fullScreen={isMobile}
      fullWidth
      open={true}
      onClose={handleCloseRelationModal}
      PaperProps={{
        sx: {
          maxWidth: "none",
          width: 800,
          height: { md: 650 }
        }
      }}
    >
      <AppBar position='relative' sx={{ boxShadow: 'none' }}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={handleCloseRelationModal}
            aria-label="close"
          >
            <Close />
          </IconButton>
          <Typography sx={{ ml: 2 }} variant="h6" component="div">
            {t("workOrders.relation")}
          </Typography>
        </Toolbar>
      </AppBar>
      <DialogContent sx={{ p: 2 }}>
        <Box height={'100%'}>
          <DataGridV2
            localSearchIdentifier={LOCAL_STORAGE_GRID_KEYS.SELECT_RELATION_MODAL_SEARCH_TEXT[keyPrefix]}
            localColumnFilterIdentifier={LOCAL_STORAGE_GRID_KEYS.SELECT_RELATION_MODAL_COLUMN_FILTER[keyPrefix]}
            localColumnOrderIdentifier={LOCAL_STORAGE_GRID_KEYS.SELECT_RELATION_MODAL_COLUMN_ORDER[keyPrefix]}
            localColumnWidthIdentifier={LOCAL_STORAGE_GRID_KEYS.SELECT_RELATION_MODAL_COLUMN_WIDTH[keyPrefix]}
            localColumnVisibilityIdentifier={LOCAL_STORAGE_GRID_KEYS.SELECT_RELATION_MODAL_COLUMN_VISIBILITY[keyPrefix]}
            apiRef={apiRef}
            hideExportBtn={true}
            loading={isLoading}
            rows={relationsResult}
            columns={columns}
            pagination
            {...(!!paginationModel && { paginationModel })}
            onPaginationModelChange={onPaginationModelChange}
            toolbarProps={getToolbarProps}
            filterMode="client"
            onRowSelectionModelChange={(newRowSelectionModel) => {
              setRowSelectionModel(newRowSelectionModel);
            }}
            {...(!!filterModel && { filterModel })}
            onFilterModelChange={onFilterModelChange}
            columnVisibilityModel={defaultColumnVisibility}
          />
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default SelectRelationModal;
